/**
 * A sheet core's row structure (`SheetHandleCore`): its tree of rows as
 * parsed (`Unresolved`) and resolved, the generation every structural change
 * bumps, the chrome's (system and footer) row sets, and the memo of the chrome
 * last installed. Every structural write of the core goes through here —
 * insert at the root, remove, reparent, swap the chrome — and is mirrored into
 * the rows-side sheet inside `mirror`, so the core's own structural
 * subscription can tell its echo from a change made elsewhere. The core hands
 * it a narrow host (`SheetCoreTree.Host`).
 */
import type { Option } from "@tento-lona/sheets";
import { OrderedTree } from "@tento-core/ordered-tree";
import { Rational } from "@tento-core/rational";
import { readonlySetView } from "@tento-core/readonly-set-view";
import { RowLocalId, RowNetworkKey, SheetId, SheetNetworkKey, type SystemRowDef } from "@tento-lona/sheets";
import type { Sheet } from "@tento-lona/sheets/platform";
import { sheetWiring } from "@tento-lona/sheets/internal-sdk";
import { type Actor, type InvalidationSink, LayoutChanged, NewRowInserted, SheetStructureChanged } from "../invalidation.ts";
import type { Resolved, RowNode, Unresolved } from "../rows/row-node.ts";
import type { SheetRow } from "../rows/sheet-row.ts";
import type { SheetHandle } from "./sheet-handle.ts";

export namespace SheetCoreTree {
  /** What the structure reaches of its core. */
  export interface Host {
    /** The client's row registry (an inserted row's wire entry is read from it). */
    readonly rows: SheetRow.Registry;
    /** Resolve a tree as parsed against the client's ids and rows (`RowNode.resolveTree`). */
    readonly resolve: (unresolved: RowNode.Tree<typeof Unresolved>) => RowNode.Tree<typeof Resolved>;
    /** Build the tree as parsed from its flattened shape (`new RowNode.Tree`, ordered by position). */
    readonly rebuild: (flat: OrderedTree.Flattened<RowLocalId, Rational>) => RowNode.Tree<typeof Unresolved>;
    /** The client's sink: structural changes are announced there. */
    readonly invalidation: InvalidationSink;
    /** The actor the structure's own announcements carry (`SheetHandle.ACTOR`). */
    readonly actor: Actor;
    /** The sheet's id as the wire spells it (it changes on a promote). */
    readonly sheetKey: () => string;
    /** The core's rows-side sheet, which every structural write mirrors into. */
    readonly rowsSheet: () => Sheet;
    /** A row was inserted at the root (the core marks a plain user row's cells known-empty). */
    readonly insertedAtRoot: (id: RowLocalId) => void;
  }
}

/** One core's row structure: see the module comment. */
export class SheetCoreTree {
  readonly #host: SheetCoreTree.Host;
  #unresolved: RowNode.Tree<typeof Unresolved>;
  #resolved: Option<RowNode.Tree<typeof Resolved>> = null;
  /** Incremented on every mutation that invalidates the resolved tree. */
  #generation = 0;
  /** The chrome's row ids; swapped only by `replaceSystemRows`. Read through `systemRowIds`. */
  readonly #systemRows: Set<RowLocalId>;
  /** The footer surface's row ids (a subset of the chrome). Read through `footerRowIds`. */
  readonly #footerRows: Set<RowLocalId>;
  /** `systemRowIds` / `footerRowIds`: refusing live views of the two sets, built once. */
  readonly #systemRowsView: ReadonlySet<RowLocalId>;
  readonly #footerRowsView: ReadonlySet<RowLocalId>;
  /**
   * True while the core is writing a structural change it has ALREADY
   * accounted for INTO the rows-side sheet — the system-row mirror and the
   * promote re-key. The core's structural subscription exists for rows-side
   * mutations that BYPASS it; the echo of its own write is not one, and
   * bumping for it re-renders for nothing: one system-row swap emits five
   * rows-side events (four `NewRowInserted` + one `SheetStructureChanged`), so
   * a rebuild that landed inside a promote's await window moved `generation`
   * by six and tripped `cow-promote-rerender`. Only ever held across
   * SYNCHRONOUS mirror writes, so no genuine concurrent structural event can
   * interleave and be swallowed.
   */
  #mirroring = false;
  /**
   * `JSON.stringify` of the system rows the last chrome rebuild installed, or
   * `null` when a direct `replaceSystemRows` has since swapped the chrome. A
   * rebuild that recomputes the same rows is a no-op and must not bump
   * `generation`.
   */
  #signature: Option<string> = null;

  constructor(
    host: SheetCoreTree.Host,
    unresolved: RowNode.Tree<typeof Unresolved>,
    systemRowIds: Set<RowLocalId>,
    footerRowIds: Set<RowLocalId>,
  ) {
    this.#host = host;
    this.#unresolved = unresolved;
    this.#systemRows = systemRowIds;
    this.#footerRows = footerRowIds;
    this.#systemRowsView = readonlySetView(systemRowIds);
    this.#footerRowsView = readonlySetView(footerRowIds);
  }

  /** Incremented on every mutation that invalidates the resolved tree. */
  get generation(): number {
    return this.#generation;
  }

  /** The chrome's row ids, as a live view that refuses every write. */
  get systemRowIds(): ReadonlySet<RowLocalId> {
    return this.#systemRowsView;
  }

  /** The footer surface's row ids — a subset of `systemRowIds` — as a live view that refuses every write. */
  get footerRowIds(): ReadonlySet<RowLocalId> {
    return this.#footerRowsView;
  }

  /** The live chrome and footer sets themselves, for the joined tree the core builds (`SheetRowTree`). */
  joinedTreeSets(): { readonly systemRowIds: Set<RowLocalId>; readonly footerRowIds: Set<RowLocalId> } {
    return { systemRowIds: this.#systemRows, footerRowIds: this.#footerRows };
  }

  /** Every row the tree places (nested rows and the chrome included): the rows this core holds. */
  rowIds(): Set<RowLocalId> {
    const ids = new Set<RowLocalId>(OrderedTree.flatten(this.#unresolved.inner).positions.keys());
    for (const id of this.#systemRows) ids.add(id);
    return ids;
  }

  /** Whether the core is writing a mirrored structural change now (`mirror`). */
  get mirroring(): boolean {
    return this.#mirroring;
  }

  /** Drop the resolved tree and bump the generation (a structural change made elsewhere, a structural row patch). */
  invalidate(): void {
    this.#resolved = null;
    this.#generation++;
  }

  /** Arm the chrome memo with the rows a parse just installed (`JSON.stringify` of them). */
  armSignature(signature: string): void {
    this.#signature = signature;
  }

  /** Whether `signature` is the chrome last installed (a rebuild of it would change nothing). */
  hasSignature(signature: string): boolean {
    return signature === this.#signature;
  }

  /** Install a rebuilt chrome: swap it into the tree, mirror it into the rows-side sheet, remember its signature. */
  installSystemRows(
    placements: SheetHandle.SystemRowPlacement[],
    rows: readonly SystemRowDef[],
    signature: string,
  ): void {
    this.replaceSystemRows(placements);
    // Both halves of ONE swap: `replaceSystemRows` already bumped `generation`,
    // so the rows-side mirror must not bump it five more times through the
    // structural subscription.
    this.mirrorSystemRows(rows);
    // Memoize AFTER the swap: `replaceSystemRows` clears the signature, so a
    // direct (non-rebuild) swap always re-arms the comparison.
    this.#signature = signature;
  }

  /** The tree as parsed (its rows' ids not yet resolved). */
  get unresolved(): RowNode.Tree<typeof Unresolved> {
    return this.#unresolved;
  }

  /** The resolved tree, memoized until the next structural change. */
  resolved(): RowNode.Tree<typeof Resolved> {
    if (!this.#resolved) this.#resolved = this.#host.resolve(this.#unresolved);
    return this.#resolved;
  }

  #rebuildUnresolved(
    flat: OrderedTree.Flattened<RowLocalId, Rational>,
  ): void {
    this.#unresolved = this.#host.rebuild(flat);
    this.#resolved = null;
    this.#generation++;
  }

  // ── System row management ─────────────────────────────────────

  /** Invalidate the resolved tree so lookup maps are rebuilt on next access. */
  invalidateLookupMaps(): void {
    this.#resolved = null;
    this.#generation++;
    this.#host.invalidation.emit(
      new SheetStructureChanged(this.#host.actor, SheetId.parse(this.#host.sheetKey())),
    );
  }

  /**
   * Current position of every row, keyed by `rawId` (matching
   * `RawSheet.serializeRow`, which emits `id: row.id.rawId`). Read from the
   * ordered tree, so it reflects true display order — unlike `serialize()`
   * (iterates `allRows()` insertion order) or `rows.peekAll()`. The promote uses
   * this to send each row's real position; without it the server re-numbers
   * by an arbitrary order and the promoted sheet's rows come out reversed.
   */
  rowPositionsByRawId(): Map<string, Rational> {
    const flat = OrderedTree.flatten(this.#unresolved.inner);
    const out = new Map<string, Rational>();
    for (const [localId, position] of flat.positions) {
      out.set(localId.rawId, position);
    }
    return out;
  }

  /** Add a registered row at the root level of the tree, announced as `actor`. */
  insertAtRoot(id: RowLocalId, position: Rational, actor: Actor): void {
    const flat = OrderedTree.flatten(this.#unresolved.inner);
    flat.positions.set(id, position);
    this.#rebuildUnresolved(flat);
    // Mirror the new row into the canonical rows-side sheet so
    // `joinedTree` includes it. Its descriptor comes from the row
    // registry the caller populated before pushing.
    const rs = this.#host.rowsSheet();
    const raw = this.#host.rows.raw(id);
    if (raw != null && rs.peekRow(id) == null) {
      rs.addRow({ id: RowNetworkKey.of(id), type: raw.type, timeScale: raw.timeScale });
    }
    this.#host.insertedAtRoot(id);
    // Scoped to THIS handle's sheet: the sink is shared app-wide, and an
    // unscoped insert advances every open sheet's render-cursor epoch (sdk-0
    // C8). `rowsSheet.id` is the same string this handle was parsed under
    // (`parseInputToRawSheet` carries `data.id` straight through), so the
    // rows-side subscriber's `concerns` check matches.
    this.#host.invalidation.emit(
      new NewRowInserted(actor, id, SheetNetworkKey.fromWire(this.#host.sheetKey())),
    );
  }

  // ── Private mutation helpers ──────────────────────────────────

  /** Delete a row (and its subtree) from the tree; `actor` stamps the `LayoutChanged`. */
  removeFromTree(id: RowLocalId, actor: Actor): void {
    const flat = OrderedTree.flatten(this.#unresolved.inner);

    // Remove node (and subtree) from the flat representation
    const removeSubtree = (nodeId: RowLocalId) => {
      const childIds = flat.children.get(nodeId);
      if (childIds) {
        for (const cid of childIds) removeSubtree(cid);
        flat.children.delete(nodeId);
      }
      flat.positions.delete(nodeId);
      flat.parent.delete(nodeId);
    };
    removeSubtree(id);

    // Remove from parent's children list
    for (const [, childIds] of flat.children) {
      const idx = childIds.indexOf(id);
      if (idx !== -1) {
        childIds.splice(idx, 1);
        break;
      }
    }

    this.#rebuildUnresolved(flat);
    // Mirror the deletion (row + subtree) into the canonical rows-side sheet.
    if (this.#host.rowsSheet().peekRow(id) != null) this.#host.rowsSheet().deleteRow(id);
    this.#host.invalidation.emit(new LayoutChanged(actor));
  }

  /** Move a row to a new parent and/or position. */
  reparent(
    id: RowLocalId,
    newParent: Option<RowLocalId>,
    position: Option<Rational>,
  ): void {
    const flat = OrderedTree.flatten(this.#unresolved.inner);
    const prevParentId = flat.parent.get(id) ?? null;

    // Update position if provided
    if (position) {
      flat.positions.set(id, position);
    }

    // Remove from old parent's children list
    if (prevParentId) {
      const siblings = flat.children.get(prevParentId);
      if (siblings) {
        const idx = siblings.indexOf(id);
        if (idx !== -1) siblings.splice(idx, 1);
        if (siblings.length === 0) flat.children.delete(prevParentId);
      }
      flat.parent.delete(id);
    }

    // Add to new parent's children list
    if (newParent) {
      const siblings = flat.children.get(newParent) ?? [];
      siblings.push(id);
      flat.children.set(newParent, siblings);
      flat.parent.set(id, newParent);
    }

    this.#rebuildUnresolved(flat);
    this.#mirrorReparentToRowsSheet(id, newParent ?? null);
  }

  /**
   * Mirror a structural mutation into the canonical rows-side `Sheet`.
   * `SheetRowTree` (see `joinedTree`) reads its tree structure from
   * `rowsSheet`, not the joined-side resolved tree — so every mutation
   * must sync there or the joined tree goes stale until a re-fetch.
   * Sibling order is derived from the joined-side positions in
   * `orderRowsSideSiblings`, so only the parent edge is mirrored here.
   */
  #mirrorReparentToRowsSheet(
    id: RowLocalId,
    newParent: Option<RowLocalId>,
  ): void {
    sheetWiring(this.#host.rowsSheet()).setParentLocal(id, newParent ?? null);
  }

  /**
   * Run `mirror` — a structural write this handle makes INTO the rows-side
   * sheet for a change it has already accounted for — with the rows-side
   * structural subscription's echo bump suppressed (see
   * `mirroringStructure`).
   *
   * `mirror` MUST be synchronous. The suppression is a time window, so an
   * `await` inside it would let a genuine concurrent structural event (a real
   * row insertion from sync, an alias re-expansion) land while suppressed and
   * lose its `generation` bump — the stale-until-scroll bug class the
   * subscription exists to prevent.
   */
  mirror(mirror: () => void): void {
    this.#mirroring = true;
    try {
      mirror();
    } finally {
      this.#mirroring = false;
    }
  }

  /**
   * Mirror a system-row swap into the canonical rows-side `Sheet`, without the
   * echo bump. Pairs with `replaceSystemRows` (the joined-side half, which
   * bumps `generation` once): the caller has already invalidated, so the
   * rows-side events this write emits are duplicates.
   *
   * Used by `rebuildSystemRows` and, through `sheetHandleInternals`, by
   * `SheetViewModel.clone` (sheet-view-model.ts) — the two places that swap
   * the chrome — so both halves of a swap stay one bump.
   */
  mirrorSystemRows(
    rows: readonly SystemRowDef[],
  ): void {
    const rs = this.#host.rowsSheet();
    this.mirror(() => sheetWiring(rs).replaceSystemRows(rows));
  }

  /**
   * Replace all system rows in the tree. Removes existing system rows
   * (identified by `systemRowIds`), then inserts new ones as root nodes.
   */
  replaceSystemRows(newRows: SheetHandle.SystemRowPlacement[]): void {
    // Any direct swap invalidates the rebuild memo (`systemRowsSignature`):
    // the caller built these defs itself (view-config switch), so what the
    // chrome now holds is no longer the last `rebuildSystemRows` result.
    this.#signature = null;
    const flat = OrderedTree.flatten(this.#unresolved.inner);

    // Remove all existing system rows
    for (const sysId of this.#systemRows) {
      flat.positions.delete(sysId);
      flat.parent.delete(sysId);
      flat.children.delete(sysId);
      // Remove from any parent's children list
      for (const [, childIds] of flat.children) {
        const idx = childIds.indexOf(sysId);
        if (idx !== -1) {
          childIds.splice(idx, 1);
          break;
        }
      }
    }

    // Update tracking sets
    this.#systemRows.clear();
    this.#footerRows.clear();

    // Add new system rows; wire parent→child relationships declared by each row
    for (const newRow of newRows) {
      flat.positions.set(newRow.id, newRow.position);
      this.#systemRows.add(newRow.id);
      if (newRow.isFooter) this.#footerRows.add(newRow.id);
      if (newRow.children) {
        flat.children.set(newRow.id, newRow.children);
        for (const childId of newRow.children) {
          flat.parent.set(childId, newRow.id);
        }
      }
    }

    this.#rebuildUnresolved(flat);
  }
}
